<template>
  <page-wrapper :title="title" :has-pad="true">
    <div class="wrapper">
      <div class="d-flex flex-row-reverse mb-3">
        <language-select
          v-model="language"
          @change="handleChangeLanguage"
        ></language-select>
      </div>
      <service-category-editor
        v-model="form"
        :icon-url="iconUrl"
        :disabled="$isCallCenter"
      ></service-category-editor>
      <div v-if="!$isCallCenter" class="text-end">
        <sc-button
          class="btn-remove"
          variant="danger"
          @click="handleClickRemove"
          >ลบ</sc-button
        >
        <sc-button variant="primary" @click="handleClickSave">บันทึก</sc-button>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { baanRueJaiClient, backendAdminClient } from "@/dependencies"
import { uploadPublicImage } from "@/god"
import LanguageSelect from "@/lib/components/LanguageSelect"
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import { localization } from "@/lib/mapper"
import { isEmpty } from "ramda"
import ServiceCategoryEditor from "../components/ServiceCategoryEditor"

export default {
  components: {
    PageWrapper,
    ScButton,
    ServiceCategoryEditor,
    LanguageSelect,
  },
  data() {
    const title = "รายละเอียดหมวดหมู่"
    const form = {
      name: "",
      services: [],
      iconFile: null,
    }

    const services = []
    const iconUrl = null
    const category = null

    return {
      title,
      form,
      iconUrl,
      category,
      services,
      language: localization.TH,
    }
  },
  watch: {
    "form.iconFile": {
      handler(newFile) {
        if (!newFile) return

        this.iconUrl = URL.createObjectURL(newFile)
      },
    },
  },
  created() {
    this.setup()
  },
  beforeDestroy() {
    if (this.iconUrl && this.iconUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.iconUrl)
    }
  },
  methods: {
    getRouteCategoryId() {
      const { id } = this.$route.params
      if (!id) {
        this.$message.error("ไม่พบรหัสหมวดหมู่")
        return null
      }

      return id
    },
    mapService(service) {
      const {
        id,
        name,
        short_description: shortDescription,
        description,
        icon_url: iconUrl,
      } = service
      const provider = {
        ...service.provider,
        logoUrl: service.provider.logo_url,
      }
      const route = { name: "ServiceDetailPage", params: { id } }

      return {
        id,
        iconUrl,
        name,
        shortDescription,
        description,
        provider,
        route,
      }
    },
    async setup() {
      try {
        await this.setupData()
        this.setupForm()
      } catch {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
      }
    },
    setupData() {
      return Promise.all([this.setupCategory(), this.setupServices()])
    },
    async setupCategory() {
      const category = await this.fetchData()

      this.category = category
    },
    async setupServices() {
      const { services } = await this.fetchServices()

      this.services = services.map((service) => this.mapService(service))
    },
    setupForm() {
      this.form = {
        name: this.category.name,
        services: this.services,
        iconFile: null,
      }

      this.iconUrl = this.category.icon
    },
    async fetchData() {
      const id = this.getRouteCategoryId()
      if (!id) {
        return null
      }

      const response =
        this.language === localization.TH
          ? await baanRueJaiClient.admin.subscriptions.serviceCategories.get(id)
          : await baanRueJaiClient.admin.subscriptions.serviceCategories.getLocalization(
              id,
              this.language,
            )

      return response.data.result
    },
    async fetchServices() {
      const id = this.getRouteCategoryId()
      if (!id) {
        return { services: [] }
      }

      const response =
        await baanRueJaiClient.admin.subscriptions.serviceCategories.getServices(
          id,
        )

      return response.data.result
    },
    async handleClickSave() {
      if (isEmpty(this.form.name.trim())) {
        this.$message.error("กรุณาใส่ชื่อหมวดหมู่บริการ")
        return
      }

      try {
        await this.$confirm("ยืนยันการบันทึก", { type: "warning" })
      } catch {
        return
      }

      try {
        await this.update()
        this.$router.push({ name: "ServiceCategoryListPage" })
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
      }
    },
    async handleClickRemove() {
      try {
        await this.$confirm("ยืนยันการลบ", { type: "warning" })
      } catch {
        return
      }

      try {
        await this.remove()
        this.$message.success("ลบเรียบร้อย")
        this.$router.push({ name: "ServiceCategoryListPage" })
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง")
      }
    },
    handleChangeLanguage() {
      this.setup()
    },
    async update() {
      const id = this.getRouteCategoryId()
      if (!id) {
        throw new Error("missing category id")
      }

      const params = await this.getUpdateParams()

      const response =
        this.language === localization.TH
          ? await backendAdminClient.serviceCategory.updateServiceCategory(
              id,
              params,
            )
          : await baanRueJaiClient.admin.subscriptions.serviceCategories.putLocalization(
              id,
              params,
              this.language,
            )

      return response.data.result
    },
    async getUpdateParams() {
      const { icon, service_ids: existingServiceIds } = this.category
      const { name, iconFile, services } = this.form

      const serviceIds = services.map((service) => service.id)

      const params =
        this.language === localization.TH
          ? (() => {
              const oldIdsSet = new Set(existingServiceIds)
              const newIdsSet = new Set(serviceIds)
              return {
                name: name.trim(),
                serviceIds: {
                  current: serviceIds,
                  add: serviceIds.filter((id) => !oldIdsSet.has(id)),
                  delete: existingServiceIds.filter((id) => !newIdsSet.has(id)),
                },
              }
            })()
          : { name: name.trim(), serviceIds }

      if (iconFile) {
        params.icon = await uploadPublicImage(iconFile, name, "article")
      } else {
        params.icon = icon
      }

      return params
    },
    async remove() {
      const id = this.getRouteCategoryId()
      if (!id) {
        throw new Error("missing category id")
      }

      await backendAdminClient.serviceCategory.deleteServiceCategory(id)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}

.btn-remove {
  float: left;
}
</style>
